import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService, ResponseObject } from '../../Services/auth.service';
import { NotificationsService } from '../../Services/shared/notifications.service';
import { NotificationTypes } from '../../app.enums';
import { TranslocoService } from '@jsverse/transloco';
import { slideDown } from './header.animations';

interface MegaMenuItem {
  label: string;
  icon?: string;
  routerLink?: string[];
  megaMenu?: MegaMenuSection[];
  badge?: number;
  visible: boolean;
  cssClass?: string;
}

interface MegaMenuSection {
  title: string;
  items: MegaMenuLink[];
  featured?: boolean;
  columns?: number;
}

interface MegaMenuLink {
  label: string;
  icon?: string;
  routerLink: string[];
  description?: string;
  badge?: number;
  visible: boolean;
}

@Component({
    selector: 'ds-header',
    templateUrl: './ds-header.component.html',
    styleUrls: ['./ds-header.component.scss'],
    animations: [slideDown],
    standalone: false
})
export class DsHeaderComponent implements OnInit {
  menuItems: MegaMenuItem[] = [];
  activeMenu: string | null = null;
  authUser: any;
  authConfig: any;

  // User dropdown state
  isUserMenuOpen = false;
  isFreePlanUser = false;
  proBySource: string = '';
  viewingRoleAsAdmin = false;

  // Mobile menu state
  isMobileMenuOpen = false;
  expandedMobileMenu: string | null = null;

  // Messages visibility
  showMessagesIcon = false;

  // Notification inputs from parent (app.component)
  @Input() chatmessages: any[] = [];
  @Input() notifications: any[] = [];
  @Input() countUnreadchatbox: number = 0;
  @Input() countUnreadNotificaion: number = 0;
  @Input() isChatBoxMessagesShow: boolean = false;
  @Input() showMessages: boolean = false;
  @Input() showNotifications: boolean = false;
  @Input() isLoadingMessages: boolean = false;
  @Input() isLoadingNotifications: boolean = false;

  // Free trial inputs from parent
  @Input() isOnFreeTrial: boolean = false;
  @Input() hasFreeTrialEnded: boolean = false;
  @Input() remainingTime: any = {};
  @Input() isEventOnlyArtist: boolean = false;

  // Output events for parent
  @Output() toggleMessagesEvent = new EventEmitter<Event>();
  @Output() toggleNotificationsEvent = new EventEmitter<Event>();
  @Output() chatWithUserEvent = new EventEmitter<any>();
  @Output() handleNotificationClickEvent = new EventEmitter<any>();
  @Output() navigateToMessagesListEvent = new EventEmitter<void>();
  @Output() navigateToNotificationsListEvent = new EventEmitter<void>();
  @Output() startFreeTrialEvent = new EventEmitter<void>();
  @Output() showUpgradeDialogEvent = new EventEmitter<void>();
  @Output() toggleSidebarEvent = new EventEmitter<void>();

  constructor(
    private authService: AuthService,
    private router: Router,
    private ngxSpinnerService: NgxSpinnerService,
    private notificationsService: NotificationsService,
    private transloco: TranslocoService
  ) {}

  ngOnInit() {
    this.authUser = this.authService.getCurrentUser();
    this.authConfig = this.authService.getAuthConfig();
    this.isFreePlanUser = this.authService.isFreePlanUser();
    this.proBySource = this.authService.proBySource();
    this.viewingRoleAsAdmin = this.authService.isViewingAsAdministrator();
    this.initializeMenuItems();
  }

  private initializeMenuItems() {
    const authConfig = this.authService.getAuthConfig();
    const isEventArtist = this.authService.isEventArtist();
    const isArtist = !!authConfig.IsArtist;
    const isGallery = !!authConfig.IsGallery;
    const isChatboxEnabled = this.authService.IsChatboxMessages();
    const isSocialMediaEnabled = this.authService.IsSocialMediaShares();
    const isFacebookEnabled = this.authService.IsFacebookAccounts();
    const isInstagramEnabled = this.authService.IsInstagramAccounts();

    // Set messages icon visibility
    this.showMessagesIcon = (isArtist && !isEventArtist && isChatboxEnabled) || (isGallery && isChatboxEnabled);

    this.menuItems = [
      // Dashboard - Direct Link
      {
        label: 'Dashboard',
        icon: 'pi pi-home',
        routerLink: ['/ds-dashboard'],
        visible: isArtist || isGallery
      },

      // Artworks - Mega Menu (includes Sync)
      {
        label: 'Artworks',
        icon: 'pi pi-images',
        visible: isArtist,
        megaMenu: [
          {
            title: 'Manage',
            items: [
              { label: 'Your Artworks', icon: 'pi pi-briefcase', routerLink: ['/artists', 'artworks'], description: 'View and manage portfolio', visible: true },
              { label: 'Create New', icon: 'pi pi-plus-circle', routerLink: ['/artists', 'artworks', 'create'], description: 'Upload new artwork', visible: !isEventArtist },
              { label: 'Labels Generation', icon: 'pi pi-barcode', routerLink: ['/labels-generation'], description: 'Info cards & barcodes', visible: !isEventArtist },
              { label: 'Sync Products', icon: 'pi pi-sync', routerLink: ['/sync-products'], description: 'Sync with platforms', visible: !isEventArtist }
            ]
          },
          {
            title: 'Events',
            items: [
              { label: 'Your Events', icon: 'pi pi-trophy', routerLink: ['/events', 'my-participated-events'], description: 'Participated events', visible: true }
            ]
          }
        ]
      },

      // Connect - Mega Menu (combines Galleries + Portfolio + Social)
      {
        label: 'Connect',
        icon: 'pi pi-link',
        visible: isArtist && !isEventArtist,
        megaMenu: [
          {
            title: 'Galleries',
            items: [
              { label: 'Connected Galleries', icon: 'pi pi-handshake', routerLink: ['/artists', 'connected-galleries'], description: 'Your gallery partners', visible: true },
              { label: 'Product Requests', icon: 'pi pi-inbox', routerLink: ['/artists', 'product-requests'], description: 'Gallery requests', visible: true },
              { label: 'Proposals', icon: 'pi pi-file', routerLink: ['/view-proposals'], description: 'Artwork proposals', visible: true },
              { label: 'Consignments', icon: 'pi pi-truck', routerLink: ['/view-consignments'], description: 'Dispatch docs', visible: true }
            ]
          },
          {
            title: 'Portfolio',
            items: [
              { label: 'My Portfolio', icon: 'pi pi-folder', routerLink: ['/artists', 'portfolio'], description: 'Manage portfolio', visible: true },
              { label: 'My Website', icon: 'pi pi-globe', routerLink: ['/profile', this.authService.getUserName()], description: 'Public profile', visible: true }
            ]
          },
          {
            title: 'Social Media',
            items: [
              { label: 'Social Shares', icon: 'pi pi-share-alt', routerLink: ['/social-media'], description: 'Share artworks', visible: isSocialMediaEnabled },
              { label: 'Facebook', icon: 'pi pi-facebook', routerLink: ['/facebook-accounts'], description: 'Facebook accounts', visible: isFacebookEnabled },
              { label: 'Instagram', icon: 'pi pi-instagram', routerLink: ['/instagram-account'], description: 'Instagram accounts', visible: isInstagramEnabled }
            ]
          }
        ]
      },

      // Settings - Mega Menu (includes Insights)
      {
        label: 'Settings',
        icon: 'pi pi-cog',
        visible: isArtist && !isEventArtist,
        megaMenu: [
          {
            title: 'Product Setup',
            items: [
              { label: 'Categories', icon: 'pi pi-tags', routerLink: ['/artists', 'art-wotk-categories'], description: 'Artwork categories', visible: true },
              { label: 'Series', icon: 'pi pi-list', routerLink: ['/artists', 'product-series'], description: 'Product series', visible: true },
              { label: 'Materials', icon: 'pi pi-box', routerLink: ['/artists', 'product-material'], description: 'Materials used', visible: true },
              { label: 'Features', icon: 'pi pi-star', routerLink: ['/artists', 'product-glazes'], description: 'Glazes & features', visible: true }
            ]
          },
          {
            title: 'Analytics',
            items: [
              { label: 'Legacy Dashboard', icon: 'pi pi-chart-bar', routerLink: ['/dashboard'], description: 'Classic dashboard', visible: true }
            ]
          }
        ]
      }
    ];

    // Filter out invisible items
    this.menuItems = this.menuItems.filter(item => item.visible);
    this.menuItems.forEach(item => {
      if (item.megaMenu) {
        item.megaMenu.forEach(section => {
          section.items = section.items.filter(link => link.visible);
        });
        // Remove empty sections
        item.megaMenu = item.megaMenu.filter(section => section.items.length > 0);
      }
    });
  }

  toggleMegaMenu(label: string, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.activeMenu = this.activeMenu === label ? null : label;
  }

  openMegaMenu(label: string) {
    this.activeMenu = label;
  }

  closeMegaMenu() {
    this.activeMenu = null;
  }

  isActive(item: MegaMenuItem): boolean {
    if (item.routerLink) {
      return this.router.isActive(item.routerLink.join('/'), false);
    }
    return false;
  }

  isMenuItemActive(item: MegaMenuItem): boolean {
    if (item.routerLink && this.router.isActive(item.routerLink.join('/'), false)) {
      return true;
    }

    // Check if any child menu items are active
    if (item.megaMenu) {
      for (const section of item.megaMenu) {
        for (const link of section.items) {
          if (this.router.isActive(link.routerLink.join('/'), false)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  // User menu methods
  toggleUserMenu(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.isUserMenuOpen = !this.isUserMenuOpen;
    // Close mega menu when opening user menu
    if (this.isUserMenuOpen) {
      this.closeMegaMenu();
    }
  }

  closeUserMenu() {
    this.isUserMenuOpen = false;
  }

  // Mobile menu methods
  toggleMobileMenu(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    // Close other menus when opening mobile menu
    if (this.isMobileMenuOpen) {
      this.closeMegaMenu();
      this.closeUserMenu();
    }
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
    this.expandedMobileMenu = null;
  }

  toggleMobileSubMenu(label: string) {
    this.expandedMobileMenu = this.expandedMobileMenu === label ? null : label;
  }

  logout(event: Event) {
    event.preventDefault();
    this.closeUserMenu();
    this.authService.logout().subscribe((res) => {
      window.location.reload();
    });
  }

  // Exit admin view and return to admin dashboard
  exitRoleView(event: Event) {
    event.preventDefault();
    this.ngxSpinnerService.show();
    this.authService.exitAccount().subscribe(
      (response: ResponseObject) => {
        this.ngxSpinnerService.hide();
        if (response.Success) {
          this.router.navigate(['/ds-admin-dashboard']).then(() => {
            location.reload();
          });
          return;
        }
        this.notificationsService.showNotification(this.transloco.translate('dsHeader.error'), response.ResponseMessage, NotificationTypes.Error);
      },
      (error) => {
        console.error(error);
        this.ngxSpinnerService.hide();
      }
    );
  }

  // Helper to get user's full name
  get userFullName(): string {
    if (!this.authUser) return '';
    return `${this.authUser.FirstName || ''} ${this.authUser.LastName || ''}`.trim() || 'User';
  }

  // Helper to get plan display text
  get planDisplayText(): string {
    if (this.authConfig?.IsArtist) {
      return this.isFreePlanUser ? 'Artist Free' : 'Artist Pro';
    }
    if (this.authConfig?.IsGallery) {
      return this.isFreePlanUser ? 'Gallery Free' : 'Gallery Pro';
    }
    if (this.authConfig?.IsSuperAdmin) {
      return 'Super Admin';
    }
    if (this.authConfig?.IsAdministrator) {
      return 'Administrator';
    }
    return '';
  }

  // Check if subscriptions link should be shown
  get showSubscriptionsLink(): boolean {
    return (this.authConfig?.IsArtist || this.authConfig?.IsGallery) &&
           !this.isFreePlanUser &&
           this.proBySource !== 'Artflow' &&
           this.proBySource !== 'FreeTrial';
  }

  // Notification methods - emit events to parent
  onToggleMessages(event: Event) {
    this.toggleMessagesEvent.emit(event);
  }

  onToggleNotifications(event: Event) {
    this.toggleNotificationsEvent.emit(event);
  }

  onChatWithUser(message: any) {
    this.chatWithUserEvent.emit(message);
  }

  onHandleNotificationClick(notification: any) {
    this.handleNotificationClickEvent.emit(notification);
  }

  onNavigateToMessagesList() {
    this.navigateToMessagesListEvent.emit();
  }

  onNavigateToNotificationsList() {
    this.navigateToNotificationsListEvent.emit();
  }

  // Free trial methods - emit events to parent
  onStartFreeTrial() {
    this.startFreeTrialEvent.emit();
  }

  onShowUpgradeDialog() {
    this.showUpgradeDialogEvent.emit();
  }
}
